/**
 * Maps task keys to their dedicated React Router routes.
 * Unknown task keys fall through to the coming-soon toast (handled in GetReadyPage).
 */
export const TASK_ROUTE_BY_KEY: Readonly<Record<string, string>> = {
	fill_health_questionnaire: "/questionnaire",
	book_appointment: "/get-ready/tasks/book-appointment",
	upload_documents: "/get-ready/tasks/upload-documents",
	sign_consent: "/get-ready/tasks/sign-consent",
};

const TASK_ID_ROUTE_BY_KEY: Readonly<
	Record<string, (taskId: string) => string>
> = {
	book_appointment: (taskId) => `/get-ready/tasks/${taskId}/book-appointment`,
	upload_documents: (taskId) => `/get-ready/tasks/${taskId}/upload-documents`,
	sign_consent: (taskId) => `/get-ready/tasks/${taskId}/sign-consent`,
};

/**
 * Maps actionCategory values to task-id route builders.
 * Used as a fallback when taskKey does not resolve to a known static route.
 * CDN-authored tasks carry actionCategory instead of a catalog taskKey.
 *
 * fill → /questionnaire (static, no task id required)
 * upload → /get-ready/tasks/${id}/upload-documents
 * book → /get-ready/tasks/${id}/book-appointment
 * notify → /get-ready/tasks/${id}/sign-consent
 */
const ACTION_CATEGORY_ROUTE: Readonly<
	Record<string, (taskId?: string) => string>
> = {
	fill: () => "/questionnaire",
	upload: (taskId) =>
		taskId
			? `/get-ready/tasks/${taskId}/upload-documents`
			: "/get-ready/tasks/upload-documents",
	book: (taskId) =>
		taskId
			? `/get-ready/tasks/${taskId}/book-appointment`
			: "/get-ready/tasks/book-appointment",
	notify: (taskId) =>
		taskId
			? `/get-ready/tasks/${taskId}/sign-consent`
			: "/get-ready/tasks/sign-consent",
};

export interface GetTaskRouteInput {
	taskKey: string;
	id?: string;
	/** CDN-authored action category; used as routing fallback when taskKey does not resolve. */
	actionCategory?: string;
}

/**
 * Returns the route for a task instance, or undefined if no dedicated route exists.
 *
 * Resolution order:
 * 1. Static catalog taskKey with id → durable task-id route.
 * 2. Static catalog taskKey without id → static fallback route.
 * 3. actionCategory → mapped route (CDN-authored tasks that lack a catalog taskKey).
 * 4. undefined → caller shows coming-soon toast.
 *
 * If an id is present, task-action pages receive it in the route so follow-up
 * slices can submit mutations against a durable backend task instance. Static
 * routes remain as fallbacks for pre-M005 callers and direct links.
 */
export function getTaskRoute(task: GetTaskRouteInput): string | undefined {
	// (1) + (2) Static catalog lookup — takes precedence over actionCategory.
	if (task.id) {
		const buildTaskIdRoute = TASK_ID_ROUTE_BY_KEY[task.taskKey];
		if (buildTaskIdRoute) return buildTaskIdRoute(task.id);
	}
	const staticRoute = TASK_ROUTE_BY_KEY[task.taskKey];
	if (staticRoute) return staticRoute;

	// (3) actionCategory fallback for CDN-authored tasks.
	if (task.actionCategory) {
		const buildActionRoute = ACTION_CATEGORY_ROUTE[task.actionCategory];
		if (buildActionRoute) return buildActionRoute(task.id);
	}

	// (4) Unknown — caller handles.
	return undefined;
}
